import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getInspections, setHideTax } from "@/lib/api";
import { useCanHideTax } from "@/components/common/TaxHiddenBadge";
import { toast } from "sonner";
import { Check as CheckIcon } from "lucide-react";
import { useEffect, useState } from "react";

// Work-order flags + contract record fields. Two layouts share the same field logic:
//   • <WOFlagsBar section="flags" | "all">  — inline strip (General card edit mode / legacy new-WO form)
//   • <WOContractRecord>                     — stacked label-over-input grid (Budget card, full page)
// Tax-rate lookup keeps its parent-managed `prevTaxKeyRef` because the rate is shared with other code
// paths in the parent (e.g. when the site changes).

function useDotStatus(form) {
  const [dotStatus, setDotStatus] = useState(null);
  useEffect(() => {
    if (!form.dot_inspection_required || !form.id) { setDotStatus(null); return; }
    let cancelled = false;
    setDotStatus(false);
    getInspections({ work_order_id: form.id })
      .then(r => { if (!cancelled) setDotStatus((r.data || []).some(i => i.is_dot && i.status === "complete")); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.dot_inspection_required, form.id]);
  return dotStatus;
}

const Check = ({ id, checked, onChange, label, disabled, testId, title, accent = "accent-blue-600", children }) => (
  <div className="flex items-center gap-2">
    <input type="checkbox" id={id} checked={checked} onChange={onChange} disabled={disabled}
      className={`h-4 w-4 rounded border-slate-300 ${accent}`} data-testid={testId} />
    <Label htmlFor={id} className="text-xs font-medium cursor-pointer whitespace-nowrap" title={title}>{label}</Label>
    {children}
  </div>
);

export function WOFlagChecks({ form, setForm, isEditable }) {
  const dotStatus = useDotStatus(form);
  return (
    <>
      <Check id="annual-service" checked={!!form.annual_service} testId="checkbox-annual-service" label="Annual Service"
        onChange={(e) => {
          // Removing annual service detaches the recurring schedule — a destructive enough action that we double-confirm.
          if (!e.target.checked && form.annual_service) {
            if (window.confirm("Are you sure you want to remove annual service from this customer? This will remove the recurring service schedule.")) {
              setForm({ ...form, annual_service: false });
            }
          } else {
            setForm({ ...form, annual_service: e.target.checked });
          }
        }} />
      {/* Breakout — PDFs and tables split Material and Labor into columns. Not locked by status. */}
      <Check id="wo-breakout" checked={!!form.show_breakdown} testId="checkbox-breakout" label="Breakout"
        onChange={(e) => setForm((prev) => ({ ...prev, show_breakdown: e.target.checked }))} />
      <Check id="wo-lift-required" checked={form.lift_required ?? true} testId="checkbox-wo-lift-required" label="Lift" accent="accent-amber-600" disabled={!isEditable}
        onChange={(e) => setForm((prev) => ({ ...prev, lift_required: e.target.checked }))} />
      <Check id="wo-dot-required" checked={form.dot_inspection_required || false} testId="checkbox-wo-dot-required" label="DOT Inspection" accent="accent-amber-600" disabled={!isEditable}
        onChange={(e) => setForm((prev) => ({ ...prev, dot_inspection_required: e.target.checked }))}>
        {form.dot_inspection_required && (
          dotStatus === null ? null : dotStatus ? (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="wo-dot-status-complete">Complete</span>
          ) : (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700" data-testid="wo-dot-status-pending">Not completed</span>
          )
        )}
      </Check>
      {form.field_complete && form.status !== "completed" && (
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-500 text-emerald-600 whitespace-nowrap"
              title={`Marked field complete by ${form.field_completed_by || "a technician"} — confirm to close it out`}
              data-testid="badge-wo-field-complete">
          Field complete
        </span>
      )}
    </>
  );
}

// ─── Contract-record fields ───────────────────────────────────────────────
export function HideTaxField({ form, setForm, woId, hiddenTaxAmount, custTaxOverride }) {
  const canHideTax = useCanHideTax();
  const on = !!form.hide_tax;
  const toggle = () => {
    // Display only: the jurisdiction rate and the stored tax never change.
    const hide = !on;
    setForm((prev) => ({ ...prev, hide_tax: hide }));
    if (woId) {
      setHideTax("work_order", woId, hide)
        .then(() => toast.success(hide ? "Tax hidden on this customer's documents" : "Tax will show on this customer's documents"))
        .catch((err) => {
          setForm((prev) => ({ ...prev, hide_tax: !hide }));
          toast.error(err.response?.data?.detail || "Could not change how tax is shown");
        });
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Same pill as the other flags; green + tick when on. */}
      <button type="button" role="switch" aria-checked={on} disabled={!canHideTax} onClick={toggle} data-testid="checkbox-hide-tax"
        title="Customer documents show no tax line — the tax is folded into Material. Tax is still calculated, charged and remitted."
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] disabled:cursor-not-allowed disabled:opacity-60 ${on ? "border-emerald-500 text-emerald-600" : "border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600"}`}>
        {on && <CheckIcon className="w-3 h-3" />}Hide Tax
      </button>
      {custTaxOverride && (
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800"
          title={`${form.tax_breakdown?.state} — this customer always bills at ${form.tax_rate}% (${form.tax_authority || "no tax no"}), regardless of the jobsite address`}
          data-testid="wo-customer-tax-override-badge">
          Customer rule
        </span>
      )}
    </div>
  );
}

export function TaxRateField({ form, setForm, taxOverride, openTaxOverride, closeTaxOverride, setTaxOverride }) {
  if (!(taxOverride || form.tax_rate > 0)) return <span className="text-xs text-slate-400">—</span>;
  return taxOverride ? (
    <span className="flex items-center gap-1" data-testid="wo-tax-rate-override">
      <Input type="number" step="0.01" autoFocus value={form.tax_rate}
        onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
        onBlur={closeTaxOverride}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") { e.preventDefault(); setTaxOverride(false); } }}
        className="h-7 w-20 text-xs text-right num" data-testid="input-wo-tax-override" />
      <span className="text-xs text-slate-500">%</span>
    </span>
  ) : (
    <span className="text-xs font-medium text-slate-700 num cursor-pointer hover:bg-slate-100 px-1 -mx-1 rounded select-none"
      title="Double-click to manually override the tax rate for this WO" onDoubleClick={openTaxOverride} data-testid="wo-tax-rate">
      {form.tax_rate}%
    </span>
  );
}

// Tax no: auto-filled from the jobsite by the tax decision tree, and overridable here until the WO closes.
export const TaxNoField = ({ form, setForm, isEditable, className = "" }) => (
  <Input id="wo-tax-authority" value={form.tax_authority || ""} maxLength={5}
    onChange={(e) => setForm((prev) => ({ ...prev, tax_authority: e.target.value.toUpperCase().slice(0, 5) }))}
    disabled={!isEditable} placeholder="Auto"
    title="Foundation tax authority (sales_taxes.tax_no) — auto-filled from the jobsite address; type here to override what Foundation receives"
    className={`text-xs font-mono ${className}`} data-testid="input-wo-tax-authority" />
);

export const PoNumberField = ({ form, setForm, className = "" }) => (
  <Input id="customer-po-number" value={form.customer_po_number || ""} maxLength={15}
    onChange={(e) => setForm((prev) => ({ ...prev, customer_po_number: e.target.value.slice(0, 15) }))}
    placeholder="PO #" className={`text-xs ${className}`} data-testid="input-customer-po-number" />
);

export const RetainageField = ({ form, setForm, className = "" }) => (
  <Input id="retainage-percent" type="number" min="0" max="100" step="0.01" value={form.retainage_percent ?? ""}
    onChange={(e) => setForm((prev) => ({ ...prev, retainage_percent: e.target.value === "" ? null : Number(e.target.value) }))}
    placeholder="—" className={`text-xs ${className}`} data-testid="input-retainage-percent" />
);

export const ProjectTypeField = ({ form, setForm, className = "" }) => (
  <Select value={form.project_type || "service"} onValueChange={(v) => setForm((prev) => ({ ...prev, project_type: v }))}>
    <SelectTrigger className={`text-xs ${className}`} data-testid="select-wo-project-type"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="service" data-testid="wo-project-type-opt-service">Service</SelectItem>
      <SelectItem value="service_contract" data-testid="wo-project-type-opt-service_contract">Service Contract</SelectItem>
      <SelectItem value="tm" data-testid="wo-project-type-opt-tm">T&amp;M</SelectItem>
    </SelectContent>
  </Select>
);

export const custOverride = (form, prop) => !!prop || String(form.tax_breakdown?.state || "").startsWith("CUSTOMER OVERRIDE");

// Inline strip — `section="flags"` (General card edit mode) or `"all"` (legacy new-WO form).
export function WOFlagsBar({
  form, setForm, isEditable, woId,
  custTaxOverride: custTaxOverrideProp,
  hiddenTaxAmount = 0,
  taxOverride, openTaxOverride, closeTaxOverride, setTaxOverride,
  section = "all",
}) {
  const showContract = section === "all";
  return (
    <div className="flex items-center gap-6 flex-wrap" data-testid="wo-flags-bar">
      <WOFlagChecks form={form} setForm={setForm} isEditable={isEditable} />
      {showContract && (
        <>
          <HideTaxField form={form} setForm={setForm} woId={woId} hiddenTaxAmount={hiddenTaxAmount} custTaxOverride={custOverride(form, custTaxOverrideProp)} />
          <TaxRateField form={form} setForm={setForm} taxOverride={taxOverride} openTaxOverride={openTaxOverride} closeTaxOverride={closeTaxOverride} setTaxOverride={setTaxOverride} />
          <div className="flex items-center gap-2">
            <Label htmlFor="wo-tax-authority" className="text-xs font-medium whitespace-nowrap">Tax No</Label>
            <TaxNoField form={form} setForm={setForm} isEditable={isEditable} className="h-7 w-20" />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="customer-po-number" className="text-xs font-medium whitespace-nowrap">PO #</Label>
            <PoNumberField form={form} setForm={setForm} className="h-7 w-32" />
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="retainage-percent" className="text-xs font-medium whitespace-nowrap">Retainage %</Label>
            <RetainageField form={form} setForm={setForm} className="h-7 w-20" />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs font-medium whitespace-nowrap">Project Type</Label>
            <ProjectTypeField form={form} setForm={setForm} className="h-7 w-36" />
          </div>
        </>
      )}
    </div>
  );
}

const RecordField = ({ label, htmlFor, children }) => (
  <div className="space-y-1">
    <Label htmlFor={htmlFor} className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);

// Stacked grid for the Budget card ("Contract record" on the Jobs screen).
export function WOContractRecord({
  form, setForm, isEditable, woId,
  custTaxOverride: custTaxOverrideProp,
  hiddenTaxAmount = 0,
  taxOverride, openTaxOverride, closeTaxOverride, setTaxOverride,
}) {
  const Field = RecordField;
  return (
    <div className="space-y-3" data-testid="wo-contract-record">
      <div className="grid grid-cols-2 gap-3">
        <Field label="PO #" htmlFor="customer-po-number"><PoNumberField form={form} setForm={setForm} className="h-8" /></Field>
        <Field label="Retainage %" htmlFor="retainage-percent"><RetainageField form={form} setForm={setForm} className="h-8" /></Field>
        <Field label="Project Type"><ProjectTypeField form={form} setForm={setForm} className="h-8" /></Field>
        <Field label="Tax No" htmlFor="wo-tax-authority"><TaxNoField form={form} setForm={setForm} isEditable={isEditable} className="h-8" /></Field>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Tax</span>
          <TaxRateField form={form} setForm={setForm} taxOverride={taxOverride} openTaxOverride={openTaxOverride} closeTaxOverride={closeTaxOverride} setTaxOverride={setTaxOverride} />
        </div>
        <HideTaxField form={form} setForm={setForm} woId={woId} hiddenTaxAmount={hiddenTaxAmount} custTaxOverride={custOverride(form, custTaxOverrideProp)} />
      </div>
    </div>
  );
}
